import React, { useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import type { TimeRange } from './types';
import { timeRanges } from './constants';

interface TimeRangePickerProps {
  timeRange: TimeRange;
  customFrom: string;
  customTo: string;
  customOpen: boolean;
  onPresetSelect: (range: TimeRange) => void;
  onCustomFromChange: (v: string) => void;
  onCustomToChange: (v: string) => void;
  onCustomOpenChange: (open: boolean) => void;
  onApplyCustom: () => void;
}

export function TimeRangePicker({
  timeRange,
  customFrom,
  customTo,
  customOpen,
  onPresetSelect,
  onCustomFromChange,
  onCustomToChange,
  onCustomOpenChange,
  onApplyCustom,
}: TimeRangePickerProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [portalPosition, setPortalPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!customOpen) {
      setPortalPosition(null);
      return;
    }
    if (!anchorRef.current || typeof document === 'undefined') return;
    const el = anchorRef.current;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setPortalPosition({ top: rect.bottom + 4, left: rect.left });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [customOpen]);

  const customPanel = customOpen && portalPosition !== null && (
    <div
      className="z-[9999] fixed bg-white shadow-xl p-3 border border-gray-200 rounded-lg min-w-[200px] max-w-[calc(100vw-2rem)]"
      style={{ top: portalPosition!.top, left: portalPosition!.left }}
      role="dialog"
      aria-label="Select custom date range"
    >
      <p className="mb-2 font-medium text-gray-700 text-xs">Select date range</p>
      <div className="gap-2 grid mb-3">
        <label className="text-gray-600 text-xs">
          From
          <input
            type="date"
            value={customFrom}
            onChange={(e) => onCustomFromChange(e.target.value)}
            className="block mt-1 px-2 py-2 sm:py-1.5 border border-gray-300 rounded w-full min-h-[44px] sm:min-h-0 text-sm"
          />
        </label>
        <label className="text-gray-600 text-xs">
          To
          <input
            type="date"
            value={customTo}
            onChange={(e) => onCustomToChange(e.target.value)}
            className="block mt-1 px-2 py-2 sm:py-1.5 border border-gray-300 rounded w-full min-h-[44px] sm:min-h-0 text-sm"
          />
        </label>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onApplyCustom}
          disabled={!customFrom || !customTo}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 px-2 py-2 sm:py-1.5 rounded min-h-[44px] sm:min-h-0 font-medium text-white text-xs touch-manipulation disabled:cursor-not-allowed"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={() => onCustomOpenChange(false)}
          className="hover:bg-gray-50 px-2 py-2 sm:py-1.5 border border-gray-300 rounded min-h-[44px] sm:min-h-0 font-medium text-gray-700 text-xs touch-manipulation"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div ref={anchorRef} className="relative flex items-center gap-0.5 bg-gray-100 p-0.5 rounded min-w-0 max-w-full">
        <div className="flex flex-nowrap items-center gap-0.5">
          {timeRanges.map((range) => (
            <button
              key={range.value}
              type="button"
              onClick={() => onPresetSelect(range)}
              className="flex-shrink-0 px-1.5 py-0.5 rounded font-medium text-[10px] transition-all"
              style={{
                backgroundColor: timeRange.value === range.value && (range.value !== 'custom' || !customOpen) ? 'white' : 'transparent',
                color: timeRange.value === range.value && (range.value !== 'custom' || !customOpen) ? '#111' : '#4b5563',
                boxShadow: timeRange.value === range.value && (range.value !== 'custom' || !customOpen) ? '0 1px 2px 0 rgb(0 0 0 / 0.05)' : 'none',
              }}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>
      {typeof document !== 'undefined' && createPortal(customPanel, document.body)}
    </>
  );
}
